import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Box, Button, Container, TextField, Typography} from '@mui/material'
import styles from './styles'
import _ from 'lodash'
import {useRouteMatch} from 'react-router'
import {useDispatch, useSelector} from 'react-redux'
import {actions as userActions} from '../../redux/modules/users'
import {RootState} from 'redux/rootReducer'
import AlertMessage from '../../components/AlertMessage'

const MIN_PASSWORD_LENGTH = 10

const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [formError, setFormError] = useState<string | undefined>()
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const params = useRouteMatch<{userId: string; resetPasswordToken: string}>()
  const {userId, resetPasswordToken} = params.params
  const userError = useSelector(({users}: RootState) => users.error)
  const loading = useSelector(({users}: RootState) => users.loading)
  const dispatch = useDispatch()
  // React state is batched/async, so it can't reliably block a second submit
  // that fires (via a fast double-click, or Enter + click) before the first
  // re-render lands. A ref is checked and set synchronously, so the second
  // call always sees the lock from the first — no race.
  const submittingRef = useRef(false)

  // The saga's loading flag flips true -> false once the request settles,
  // regardless of whether userError changed value — that's what tells us
  // the response actually came back, so we know whether to show success.
  useEffect(() => {
    if (!submitting || loading) {
      return
    }
    submittingRef.current = false
    setSubmitting(false)
    if (userError) {
      return
    }
    setSuccess(true)
    setPassword('')
    setPasswordConfirm('')
  }, [loading])

  const resetPassword = () => {
    if (submittingRef.current) {
      return
    }
    setFormError(undefined)
    setSuccess(false)
    if (_.isEmpty(password) || _.isEmpty(passwordConfirm)) {
      setFormError('All fields are required.')
      return
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setFormError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }
    if (password !== passwordConfirm) {
      setFormError('Passwords do not match.')
      return
    }
    submittingRef.current = true
    setSubmitting(true)
    dispatch(userActions.resetPassword({userId, resetPasswordToken, password}))
  }
  const errorMessage = useMemo(() => {
    if (formError) {
      return formError
    }

    if (userError) {
      return userError
    }

    return undefined
  }, [formError, userError])

  return (
    <Container component="main" sx={styles.root}>
      <Box sx={styles.form} textAlign="center">
        <Typography sx={styles.headerLabel} variant="h5">
          Please choose a new password
        </Typography>
        <Typography sx={styles.helperText} variant="body2">
          Must be at least {MIN_PASSWORD_LENGTH} characters.
        </Typography>

        <AlertMessage error={errorMessage} severity="error" sx={{marginBottom: 2, width: 300}} />
        {success && (
          <AlertMessage
            error="Your password has been changed. You can now log in."
            severity="success"
            sx={{marginBottom: 2, width: 300}}
          />
        )}

        <TextField
          sx={styles.textField}
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Password"
          type="password"
          onKeyDown={args => args.key === 'Enter' && resetPassword()}
        />
        <TextField
          sx={styles.textField}
          value={passwordConfirm}
          onChange={e => setPasswordConfirm(e.target.value)}
          placeholder="Confirm password"
          type="password"
          onKeyDown={args => args.key === 'Enter' && resetPassword()}
        />
        <Button
          variant="contained"
          sx={styles.submitButton}
          onClick={resetPassword}
          disabled={submitting}>
          Submit
        </Button>
      </Box>
    </Container>
  )
}
export default ResetPasswordPage
